import React, { FC, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Phase, PhaseUpdate, Task, PricingLevel } from "@design-system";
import { GanttChart } from "./gantt-chart";
import { BudgetTable } from "./budget-table";
import { WorkplanTable } from "./workplan-table";
import { DatePicker } from "../date-picker";
import { Tabs, Select, Space, Button, Modal, Typography } from "antd";
import {
  ThunderboltOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import styles from "./matter-timeline.module.css";
import { PeriodSelector } from "./period-selector";
import { VPDLabel } from "~/context/taxonomy-context";
import { CustomiseTimelineModal, CustomTimelineSaveData } from "./customise-timeline-modal";
import {
  calculateEvenDistributionPercentages,
  calculateProportionateDistributionPercentages,
  hasTimeEstimates as checkHasTimeEstimates,
} from "./timeline-distribution-utils";

const { RangePicker } = DatePicker;

const {
  ["timeline-header"]: timelineHeaderClassName,
  ["timeline-header-left"]: timelineHeaderLeftClassName,
  ["timeline-header-controls"]: timelineHeaderControlsClassName,
  ["timeline-header-right"]: timelineHeaderRightClassName,
  ["timeline-autofill-button-container"]: timelineAutofillButtonContainerClassName,
  ["timeline-autofill-primary-button"]: timelineAutofillPrimaryButtonClassName,
  ["timeline-autofill-custom-button"]: timelineAutofillCustomButtonClassName,
  ["timeline-helper-text"]: timelineHelperTextClassName,
  ["timeline-autofill-button-pulse"]: timelineAutofillButtonPulseClassName,
} = styles as any;

const TimelineHelperText: FC<{
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
  phases: Phase[];
  warnings: {
    hasItemsBeforeStart: boolean;
    hasItemsAfterEnd: boolean;
    hasItemsOutsideBothEnds: boolean;
  } | null;
  applyAutofillPulse: () => void;
}> = ({ matterStartDate, matterDeadlineDate, phases, warnings, applyAutofillPulse }) => {
  // Check if any phases or tasks have start or deadline dates
  const hasAnyDates = useMemo(() => {
    return phases.some(
      (phase) =>
        phase.startDate ||
        phase.deadlineDate ||
        phase.tasks?.some((task) => task.startDate || task.deadlineDate)
    );
  }, [phases]);

  useEffect(() => {
    if (!hasAnyDates && phases.length > 0) {
      applyAutofillPulse();
    }
  }, [applyAutofillPulse, hasAnyDates, phases.length]);

  // Display warnings if they exist
  if (
    warnings &&
    (warnings.hasItemsBeforeStart || warnings.hasItemsAfterEnd || warnings.hasItemsOutsideBothEnds)
  ) {
    return (
      <div className={`${timelineHelperTextClassName} error`} style={{ width: "auto" }}>
        <ExclamationCircleOutlined />
        <div className="timeline-helper-warnings">
          {warnings.hasItemsBeforeStart && (
            <div>Warning: Some phases/tasks start before the matter start date</div>
          )}
          {warnings.hasItemsAfterEnd && (
            <div>Warning: Some phases/tasks end after the matter deadline date</div>
          )}
          {warnings.hasItemsOutsideBothEnds && (
            <div>
              Warning: Some phases/tasks extend beyond both the matter start and deadline dates
            </div>
          )}
        </div>
      </div>
    );
  }

  // If there are no phases, show a message to add phases
  if (phases.length === 0) {
    return (
      <div className={timelineHelperTextClassName} style={{ width: "auto" }}>
        <ExclamationCircleOutlined /> Add phases to your matter to enable timeline functionality.
      </div>
    );
  }

  if (!matterStartDate || !matterDeadlineDate) {
    return (
      <div className={timelineHelperTextClassName} style={{ width: "auto" }}>
        <ExclamationCircleOutlined /> For best results, set the matter start and deadline dates
        above.
      </div>
    );
  }

  if (!hasAnyDates && phases.length > 0) {
    // Remove the useEffect from here, it's now at the top of the component
    return (
      <div className={timelineHelperTextClassName} style={{ width: "auto" }}>
        <ExclamationCircleOutlined /> Click &quot;Autofill Timeline&quot; to automatically fill in
        timeline dates, or manually set dates by clicking and dragging on the Gantt chart.
      </div>
    );
  }

  return null;
};

type Props = {
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
  pricingLevel: PricingLevel;
  phases: Phase[];
  onUpdateMatterDates: (startDate: Date | null, deadlineDate: Date | null) => void;
  onUpdatePhase: (phase: PhaseUpdate) => void;
  onUpdateTask: (task: Partial<Task>, phaseId: number) => void;
  onUpdatePhases: (phases: PhaseUpdate[]) => void;
  onUpdateTasks: (tasks: Partial<Task>[]) => void;
  currencySymbol: string;
  matterId: number;
  timelineActivationKey: number;
};

type Period = {
  type: "year" | "quarter" | "month";
  value: string;
} | null;

type FilterSelectorProps = {
  phases: Phase[];
  selectedPracticeArea: string | null;
  selectedFeeEarnerLevel: string | null;
  onPracticeAreaChange: (value: string | null) => void;
  onFeeEarnerLevelChange: (value: string | null) => void;
};

const FilterSelector: FC<FilterSelectorProps> = ({
  phases,
  selectedPracticeArea,
  selectedFeeEarnerLevel,
  onPracticeAreaChange,
  onFeeEarnerLevelChange,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea", { textCase: "capitalise" });
  const FEL_LABEL = VPDLabel("FeeEarnerLevel", { textCase: "capitalise" });

  // Extract unique practice areas and fee earner levels from phases
  const { practiceAreas, feeEarnerLevels } = useMemo(() => {
    const practiceAreasSet = new Set<string>();
    const feeEarnerLevelsSet = new Set<string>();

    phases.forEach((phase) => {
      phase.work?.forEach((work) => {
        if (work.feeEarner?.practiceArea?.name) {
          practiceAreasSet.add(work.feeEarner.practiceArea.name);
        }
        if (work.feeEarnerLevel?.description) {
          feeEarnerLevelsSet.add(work.feeEarnerLevel.description);
        }
      });
    });

    return {
      practiceAreas: Array.from(practiceAreasSet).sort(),
      feeEarnerLevels: Array.from(feeEarnerLevelsSet).sort(),
    };
  }, [phases]);

  return (
    <Space size="middle">
      {practiceAreas.length > 0 && (
        <Select
          style={{ width: 200 }}
          placeholder={`Filter by ${PA_LABEL}`}
          allowClear
          value={selectedPracticeArea}
          onChange={onPracticeAreaChange}
          options={practiceAreas.map((area) => ({ label: area, value: area }))}
        />
      )}
      {feeEarnerLevels.length > 0 && (
        <Select
          style={{ width: 200 }}
          placeholder={`Filter by ${FEL_LABEL}`}
          allowClear
          value={selectedFeeEarnerLevel}
          onChange={onFeeEarnerLevelChange}
          options={feeEarnerLevels.map((level) => ({ label: level, value: level }))}
        />
      )}
    </Space>
  );
};

const getDateRangeForPeriod = (period: Period): { startDate: Date; endDate: Date } | null => {
  if (!period) return null;

  const { type, value } = period;
  switch (type) {
    case "year": {
      return {
        startDate: dayjs(`${value}-01-01`).startOf("year").toDate(),
        endDate: dayjs(`${value}-12-31`).endOf("year").toDate(),
      };
    }
    case "quarter": {
      const [year, quarter] = value.split("-Q");
      const startDate = dayjs(`${year}-01-01`).quarter(parseInt(quarter)).startOf("quarter");
      return {
        startDate: startDate.toDate(),
        endDate: startDate.endOf("quarter").toDate(),
      };
    }
    case "month": {
      const [year, month] = value.split("-");
      const startDate = dayjs(`${year}-${month}-01`).startOf("month");
      return {
        startDate: startDate.toDate(),
        endDate: startDate.endOf("month").toDate(),
      };
    }
    default:
      return null;
  }
};

export const MatterTimeline: FC<Props> = ({
  matterStartDate,
  matterDeadlineDate,
  pricingLevel,
  phases,
  onUpdateMatterDates,
  onUpdatePhase,
  onUpdateTask,
  onUpdatePhases,
  onUpdateTasks,
  currencySymbol,
  matterId,
  timelineActivationKey,
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(null);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string | null>(null);
  const [selectedFeeEarnerLevel, setSelectedFeeEarnerLevel] = useState<string | null>(null);
  const [isCustomiseModalVisible, setIsCustomiseModalVisible] = useState(false);
  const [isAutofillChoiceModalVisible, setIsAutofillChoiceModalVisible] = useState(false);
  const [isAutofillConfirmModalVisible, setIsAutofillConfirmModalVisible] = useState(false);
  const [isAutofillButtonPulsing, setIsAutofillButtonPulsing] = useState(false);

  const autofillButtonRef = useRef<HTMLButtonElement>(null);

  const applyAutofillPulse = useCallback(() => {
    setIsAutofillButtonPulsing(true);
  }, []);

  // Check if any phases or tasks have timeline data already set
  const hasExistingTimelineData = useMemo(() => {
    return phases.some(
      (phase) =>
        phase.startDate ||
        phase.deadlineDate ||
        phase.tasks?.some((task) => task.startDate || task.deadlineDate)
    );
  }, [phases]);

  // Calculate fallback dates from phases and tasks when matter dates are null
  const fallbackDates = useMemo(() => {
    let earliestStartDate: Date | null = null;
    let latestEndDate: Date | null = null;

    phases.forEach((phase) => {
      // Check phase dates
      if (phase.startDate && (!earliestStartDate || phase.startDate < earliestStartDate)) {
        earliestStartDate = phase.startDate;
      }
      if (phase.deadlineDate && (!latestEndDate || phase.deadlineDate > latestEndDate)) {
        latestEndDate = phase.deadlineDate;
      }

      // Check task dates
      phase.tasks?.forEach((task) => {
        if (task.startDate && (!earliestStartDate || task.startDate < earliestStartDate)) {
          earliestStartDate = task.startDate;
        }
        if (task.deadlineDate && (!latestEndDate || task.deadlineDate > latestEndDate)) {
          latestEndDate = task.deadlineDate;
        }
      });
    });

    return { earliestStartDate, latestEndDate };
  }, [phases]);

  // Filter phases based on selected practice area and fee earner level
  const filteredPhases = useMemo(() => {
    if (!selectedPracticeArea && !selectedFeeEarnerLevel) {
      return phases;
    }

    return phases.map((phase) => {
      // Filter work items based on selected filters
      const filteredWork = phase.work?.filter((work) => {
        const matchesPracticeArea =
          !selectedPracticeArea || work.feeEarner?.practiceArea?.name === selectedPracticeArea;
        const matchesFeeEarnerLevel =
          !selectedFeeEarnerLevel || work.feeEarnerLevel?.description === selectedFeeEarnerLevel;
        return matchesPracticeArea && matchesFeeEarnerLevel;
      });

      // Return phase with filtered work items
      return {
        ...phase,
        work: filteredWork || null,
      };
    });
  }, [phases, selectedPracticeArea, selectedFeeEarnerLevel]);

  const warnings = useMemo(() => {
    if (!matterStartDate || !matterDeadlineDate) return null;

    // Create start-of-day versions of matter dates
    const matterStartMidnight = dayjs(matterStartDate).startOf("day").toDate();
    const matterDeadlineMidnight = dayjs(matterDeadlineDate).startOf("day").toDate();

    const hasItemsOutsideBothEnds = filteredPhases.some(
      (phase) =>
        (phase.startDate &&
          phase.deadlineDate &&
          dayjs(phase.startDate).startOf("day").toDate() < matterStartMidnight &&
          dayjs(phase.deadlineDate).startOf("day").toDate() > matterDeadlineMidnight) ||
        phase.tasks?.some(
          (task) =>
            task.startDate &&
            task.deadlineDate &&
            dayjs(task.startDate).startOf("day").toDate() < matterStartMidnight &&
            dayjs(task.deadlineDate).startOf("day").toDate() > matterDeadlineMidnight
        )
    );

    const hasItemsBeforeStart = filteredPhases.some(
      (phase) =>
        (phase.startDate && dayjs(phase.startDate).startOf("day").toDate() < matterStartMidnight) ||
        phase.tasks?.some(
          (task) =>
            task.startDate && dayjs(task.startDate).startOf("day").toDate() < matterStartMidnight
        )
    );

    const hasItemsAfterEnd = filteredPhases.some(
      (phase) =>
        (phase.deadlineDate &&
          dayjs(phase.deadlineDate).startOf("day").toDate() > matterDeadlineMidnight) ||
        phase.tasks?.some(
          (task) =>
            task.deadlineDate &&
            dayjs(task.deadlineDate).startOf("day").toDate() > matterDeadlineMidnight
        )
    );

    return { hasItemsBeforeStart, hasItemsAfterEnd, hasItemsOutsideBothEnds };
  }, [filteredPhases, matterStartDate, matterDeadlineDate]);

  const selectedDateRange = useMemo(() => getDateRangeForPeriod(selectedPeriod), [selectedPeriod]);

  const tabBarExtraContent = (
    <Space size="large">
      {pricingLevel === PricingLevel.FeeEarner && (
        <FilterSelector
          phases={phases}
          selectedPracticeArea={selectedPracticeArea}
          selectedFeeEarnerLevel={selectedFeeEarnerLevel}
          onPracticeAreaChange={setSelectedPracticeArea}
          onFeeEarnerLevelChange={setSelectedFeeEarnerLevel}
        />
      )}
      <PeriodSelector
        matterStartDate={matterStartDate || fallbackDates.earliestStartDate}
        matterDeadlineDate={matterDeadlineDate || fallbackDates.latestEndDate}
        value={selectedPeriod}
        onChange={setSelectedPeriod}
      />
    </Space>
  );

  // Check if any phase's works have time values > 0
  const hasTimeEstimates = useMemo(() => checkHasTimeEstimates(phases), [phases]);

  // Function to confirm clearing all timeline data
  const confirmClearTimeline = (callback: () => void) => {
    modal.confirm({
      width: 600,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Typography.Text>
          Are you sure you want to clear all timeline dates? This will remove all start and deadline
          dates from phases and tasks.
        </Typography.Text>
      ),
      onOk: callback,
      onCancel() {},
    });
  };

  // Function to clear all timeline data
  const handleClearTimeline = () => {
    if (phases.length === 0) return;

    // Clear all phase dates
    const phaseUpdates: PhaseUpdate[] = phases.map((phase) => ({
      id: phase.id,
      startDate: null,
      deadlineDate: null,
    }));

    // Clear all task dates
    const taskUpdates: Partial<Task>[] = phases.flatMap(
      (phase) =>
        phase.tasks?.map((task) => ({
          id: task.id,
          startDate: null,
          deadlineDate: null,
        })) || []
    );

    if (phaseUpdates.length > 0) onUpdatePhases(phaseUpdates);
    if (taskUpdates.length > 0) onUpdateTasks(taskUpdates);
  };

  // Function to handle autofill button click
  const handleAutofillButtonClick = () => {
    setIsAutofillButtonPulsing(false);

    if (!matterStartDate || !matterDeadlineDate || phases.length === 0) {
      return;
    }

    // If there's existing timeline data, show the confirmation modal first
    if (hasExistingTimelineData) {
      setIsAutofillConfirmModalVisible(true);
      return;
    }

    // Otherwise proceed directly
    if (hasTimeEstimates) {
      setIsAutofillChoiceModalVisible(true);
    } else {
      handleEvenDistributionAutofill();
    }
  };

  // Handle confirmation of replacing existing timeline data
  const handleConfirmReplaceTimeline = () => {
    setIsAutofillConfirmModalVisible(false);

    if (hasTimeEstimates) {
      setIsAutofillChoiceModalVisible(true);
    } else {
      handleEvenDistributionAutofill();
    }
  };

  // Function to distribute phases and tasks evenly across the timeline (Default Autofill)
  const handleEvenDistributionAutofill = () => {
    if (!matterStartDate || !matterDeadlineDate) {
      return;
    }
    if (phases.length === 0) {
      return;
    }

    // Calculate total days between matter start and deadline
    const totalDurationDays = dayjs(matterDeadlineDate).diff(matterStartDate, "day");
    if (totalDurationDays <= 0) {
      return;
    }

    const phasePercentages = calculateEvenDistributionPercentages(phases);
    const phaseUpdates: PhaseUpdate[] = [];
    const taskUpdates: Partial<Task>[] = [];

    let currentStartDate = dayjs(matterStartDate);

    phasePercentages.forEach(({ phaseId, percentage }) => {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;

      const phaseDurationDays = (percentage / 100) * totalDurationDays;
      const phaseStartDate = currentStartDate.toDate();
      const phaseEndDate = currentStartDate.add(phaseDurationDays, "day").toDate();

      phaseUpdates.push({
        id: phaseId,
        startDate: phaseStartDate,
        deadlineDate: phaseEndDate,
      });

      // Distribute tasks within this phase
      if (phase.tasks && phase.tasks.length > 0) {
        const taskCount = phase.tasks.length;
        const phaseDurationDays = dayjs(phaseEndDate).diff(phaseStartDate, "day");
        const daysPerTask = phaseDurationDays > 0 ? phaseDurationDays / taskCount : 0;

        phase.tasks.forEach((task, taskIndex) => {
          const taskStartDate = dayjs(phaseStartDate)
            .add(taskIndex * daysPerTask, "day")
            .startOf("day")
            .toDate();
          const taskEndDate = dayjs(phaseStartDate)
            .add((taskIndex + 1) * daysPerTask, "day")
            .endOf("day")
            .toDate();
          const adjustedTaskEndDate = taskIndex === taskCount - 1 ? phaseEndDate : taskEndDate;

          taskUpdates.push({
            id: task.id,
            startDate: taskStartDate,
            deadlineDate: adjustedTaskEndDate,
          });
        });
      }

      currentStartDate = dayjs(phaseEndDate);
    });

    if (phaseUpdates.length > 0) onUpdatePhases(phaseUpdates);
    if (taskUpdates.length > 0) onUpdateTasks(taskUpdates);

    setIsAutofillChoiceModalVisible(false);
  };

  // Function to distribute phases proportionate to their work time estimates
  const handleProportionateDistributionAutofill = () => {
    if (!matterStartDate || !matterDeadlineDate) {
      return;
    }
    if (phases.length === 0) {
      return;
    }

    const totalDurationDays = dayjs(matterDeadlineDate).diff(matterStartDate, "day");
    if (totalDurationDays <= 0) {
      return;
    }

    const phasePercentages = calculateProportionateDistributionPercentages(phases);
    const phaseUpdates: PhaseUpdate[] = [];
    const taskUpdates: Partial<Task>[] = [];

    let currentStartDate = dayjs(matterStartDate);

    phasePercentages.forEach(({ phaseId, percentage }) => {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;

      const phaseDurationDays = (percentage / 100) * totalDurationDays;
      const phaseStartDate = currentStartDate.toDate();
      const phaseEndDate = currentStartDate.add(phaseDurationDays, "day").toDate();

      phaseUpdates.push({
        id: phaseId,
        startDate: phaseStartDate,
        deadlineDate: phaseEndDate,
      });

      // Distribute tasks within this phase
      if (phase.tasks && phase.tasks.length > 0) {
        const taskCount = phase.tasks.length;
        const phaseDurationDays = dayjs(phaseEndDate).diff(phaseStartDate, "day");
        const daysPerTask = phaseDurationDays > 0 ? phaseDurationDays / taskCount : 0;

        phase.tasks.forEach((task, taskIndex) => {
          const taskStartDate = dayjs(phaseStartDate)
            .add(taskIndex * daysPerTask, "day")
            .startOf("day")
            .toDate();
          const taskEndDate = dayjs(phaseStartDate)
            .add((taskIndex + 1) * daysPerTask, "day")
            .endOf("day")
            .toDate();
          const adjustedTaskEndDate = taskIndex === taskCount - 1 ? phaseEndDate : taskEndDate;

          taskUpdates.push({
            id: task.id,
            startDate: taskStartDate,
            deadlineDate: adjustedTaskEndDate,
          });
        });
      }

      currentStartDate = dayjs(phaseEndDate);
    });

    if (phaseUpdates.length > 0) onUpdatePhases(phaseUpdates);
    if (taskUpdates.length > 0) onUpdateTasks(taskUpdates);

    setIsAutofillChoiceModalVisible(false);
  };

  // Handler for the Customise button - opens the modal
  const handleCustomiseClick = () => {
    if (!matterStartDate || !matterDeadlineDate || phases.length === 0) {
      return;
    }
    setIsCustomiseModalVisible(true);
  };

  // Handler for saving custom percentages from the modal
  const handleSaveCustomPercentages = (customData: CustomTimelineSaveData[]) => {
    if (!matterStartDate || !matterDeadlineDate) return;

    const matterTotalDurationSeconds = dayjs(matterDeadlineDate).diff(matterStartDate, "second");
    if (matterTotalDurationSeconds <= 0) return;

    const phaseUpdates: PhaseUpdate[] = [];
    const taskUpdates: Partial<Task>[] = [];
    let currentPhaseStartDate = dayjs(matterStartDate);

    customData.forEach(({ phaseId, phasePercentage, taskPercentages }) => {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;

      // --- Calculate Phase Dates ---
      const phaseDurationSeconds = matterTotalDurationSeconds * (phasePercentage / 100);
      const phaseStartDate = currentPhaseStartDate.toDate();
      let phaseEndDate = currentPhaseStartDate.add(phaseDurationSeconds, "second").toDate();

      // Prevent phase end date exceeding matter deadline or being before start date
      if (dayjs(phaseEndDate).isAfter(matterDeadlineDate)) phaseEndDate = matterDeadlineDate;
      if (dayjs(phaseEndDate).isBefore(phaseStartDate)) phaseEndDate = phaseStartDate;

      phaseUpdates.push({
        id: phase.id,
        startDate: phaseStartDate,
        deadlineDate: phaseEndDate,
      });

      // --- Calculate Task Dates within this Phase ---
      if (phase.tasks && phase.tasks.length > 0 && taskPercentages.length > 0) {
        const currentPhaseActualDurationSeconds = dayjs(phaseEndDate).diff(
          phaseStartDate,
          "second"
        );
        let currentTaskStartDate = dayjs(phaseStartDate);

        // Ensure we only process task percentages provided by the modal for this phase
        const relevantTaskPercentages = taskPercentages.filter((tp) =>
          phase.tasks!.some((t) => t.id === tp.taskId)
        );

        // Calculate total task percentage specified *for this phase*
        // const phaseTaskTotalPercent = relevantTaskPercentages.reduce((sum, tp) => sum + tp.percentage, 0);
        // If phaseTaskTotalPercent is 0, could distribute evenly or set near zero duration
        // const effectivePhaseTaskTotalPercent = phaseTaskTotalPercent > 0 ? phaseTaskTotalPercent : 100;

        relevantTaskPercentages.forEach(({ taskId, percentage: taskPercentage }) => {
          const task = phase.tasks!.find((t) => t.id === taskId);
          if (!task) return;

          // Calculate task duration based on its percentage *of the phase's actual duration*
          const taskDurationSeconds = currentPhaseActualDurationSeconds * (taskPercentage / 100);

          const taskStartDate = currentTaskStartDate.toDate();
          let taskEndDate = currentTaskStartDate.add(taskDurationSeconds, "second").toDate();

          // Prevent task end date exceeding phase end date or being before its start date
          if (dayjs(taskEndDate).isAfter(phaseEndDate)) taskEndDate = phaseEndDate;
          if (dayjs(taskEndDate).isBefore(taskStartDate)) taskEndDate = taskStartDate;

          taskUpdates.push({
            id: task.id,
            startDate: taskStartDate,
            deadlineDate: taskEndDate,
          });

          // Move to the next task's start date based on calculated duration
          currentTaskStartDate = dayjs(taskEndDate);
        });
      }

      // Move to the next phase's start date
      currentPhaseStartDate = dayjs(phaseEndDate);
    });

    if (phaseUpdates.length > 0) onUpdatePhases(phaseUpdates);
    if (taskUpdates.length > 0) onUpdateTasks(taskUpdates);

    setIsCustomiseModalVisible(false);
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {contextHolder}
      <div className={timelineHeaderClassName}>
        <div className={timelineHeaderLeftClassName}>
          <h2>Timeline Editor</h2>
          <div className={timelineHeaderControlsClassName}>
            <span>Set matter start date and deadline:</span>
            <RangePicker
              value={[
                matterStartDate ? dayjs(matterStartDate) : null,
                matterDeadlineDate ? dayjs(matterDeadlineDate) : null,
              ]}
              onChange={(dates) => {
                onUpdateMatterDates(dates?.[0]?.toDate() || null, dates?.[1]?.toDate() || null);
              }}
              format={(value) => value?.toDate().toLocaleDateString() || ""}
              allowEmpty={[true, true]}
              style={{ width: 280 }}
            />
            {matterStartDate && matterDeadlineDate && (
              <div className={timelineAutofillButtonContainerClassName}>
                <Button
                  className={`${timelineAutofillPrimaryButtonClassName} ${
                    isAutofillButtonPulsing ? timelineAutofillButtonPulseClassName : ""
                  }`}
                  icon={<ThunderboltOutlined />}
                  onClick={handleAutofillButtonClick}
                  type="primary"
                  ref={autofillButtonRef}
                >
                  Autofill Timeline
                </Button>
                <Button
                  className={timelineAutofillCustomButtonClassName}
                  icon={<EditOutlined />}
                  onClick={handleCustomiseClick}
                  type="default"
                >
                  Customise
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className={timelineHeaderRightClassName}>
          {phases.length > 0 && (
            <Button
              icon={<ReloadOutlined />}
              style={{ marginRight: "16px" }}
              type="primary"
              onClick={() => confirmClearTimeline(handleClearTimeline)}
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
        <TimelineHelperText
          matterStartDate={matterStartDate}
          matterDeadlineDate={matterDeadlineDate}
          phases={phases}
          warnings={warnings}
          applyAutofillPulse={applyAutofillPulse}
        />
      </div>
      <div style={{ flex: 1, overflow: "hidden" }}>
        {pricingLevel === PricingLevel.FeeEarner ? (
          <Tabs
            defaultActiveKey="gantt"
            tabBarExtraContent={tabBarExtraContent}
            style={{ height: "100%" }}
            destroyInactiveTabPane
          >
            <Tabs.TabPane tab="Gantt Chart" key="gantt">
              <GanttChart
                matterStartDate={matterStartDate}
                matterDeadlineDate={matterDeadlineDate}
                phases={filteredPhases}
                onUpdatePhase={onUpdatePhase}
                onUpdateTask={onUpdateTask}
                viewStartDate={selectedDateRange?.startDate}
                viewEndDate={selectedDateRange?.endDate}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Budget Breakdown" key="budget">
              <BudgetTable
                phases={filteredPhases}
                onUpdatePhase={onUpdatePhase}
                onUpdateTask={onUpdateTask}
                currencySymbol={currencySymbol}
                viewStartDate={selectedDateRange?.startDate}
                viewEndDate={selectedDateRange?.endDate}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Workplan" key="workplan">
              <WorkplanTable matterId={matterId} timelineActivationKey={timelineActivationKey} />
            </Tabs.TabPane>
          </Tabs>
        ) : (
          <GanttChart
            matterStartDate={matterStartDate}
            matterDeadlineDate={matterDeadlineDate}
            phases={filteredPhases}
            onUpdatePhase={onUpdatePhase}
            onUpdateTask={onUpdateTask}
            viewStartDate={selectedDateRange?.startDate}
            viewEndDate={selectedDateRange?.endDate}
          />
        )}
      </div>

      {/* Autofill Choice Modal */}
      <Modal
        title="Choose Autofill Method"
        open={isAutofillChoiceModalVisible}
        onCancel={() => setIsAutofillChoiceModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <p>Choose how to distribute phases on the timeline:</p>
          <Button
            type="primary"
            onClick={handleEvenDistributionAutofill}
            style={{ marginBottom: "8px" }}
          >
            Distribute Evenly
          </Button>
          <Button type="primary" onClick={handleProportionateDistributionAutofill}>
            Distribute Proportionately to Time Estimates
          </Button>
        </div>
      </Modal>

      {/* Autofill Confirmation Modal */}
      <Modal
        title="Replace Existing Timeline Data"
        open={isAutofillConfirmModalVisible}
        onCancel={() => setIsAutofillConfirmModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsAutofillConfirmModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmReplaceTimeline}>
            Continue
          </Button>,
        ]}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div
            style={{
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: "4px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              style={{ color: "#ff4d4f", marginRight: "8px", fontSize: "16px" }}
            />
            <span style={{ color: "#ff4d4f", fontWeight: "500" }}>
              This will replace all existing timeline data
            </span>
          </div>
          <p>
            The current start and end dates for all phases and tasks will be overwritten. Are you
            sure you want to continue?
          </p>
        </div>
      </Modal>

      <CustomiseTimelineModal
        phases={phases}
        visible={isCustomiseModalVisible}
        onClose={() => setIsCustomiseModalVisible(false)}
        onSave={handleSaveCustomPercentages}
        matterStartDate={matterStartDate}
        matterDeadlineDate={matterDeadlineDate}
      />
    </div>
  );
};
